import React from "react";
import { capabilityState, capabilityEta, capabilityLimit } from "./capabilities";

/**
 * The tenant's capability map, as the console consumes it.
 *
 * Kept apart from the provider (EntitlementsProvider.jsx) on purpose: this file has no
 * transport, so every page that only ASKS (`granted`, `status`, `limit`) imports it for
 * real under Jest without mocking `@/lib/api`.
 *
 * The context's default value is the LOADING state, not a guess: a consumer rendered
 * before the map arrives — or outside a provider, as a page rendered on its own in a test
 * is — is in exactly the position of the first frame of the console. In that state no
 * capability is usable (`granted` is false), no status is claimed (`status` is null) and
 * no limit is stated (`limit` is null), so nothing is advertised and nothing is refused
 * on the client; the server stays the authority.
 */
export const buildEntitlements = ({ phase, planId = null, capabilities = null, reload = () => {} }) => ({
  /** "loading" | "ready" | "error" */
  phase,
  planId,
  reload,
  granted: (key) => phase === "ready" && capabilityState(capabilities, key) === "granted",
  status: (key) => (phase === "ready" ? capabilityState(capabilities, key) : null),
  eta: (key) => (phase === "ready" ? capabilityEta(capabilities, key) : null),
  limit: (key) => (phase === "ready" ? capabilityLimit(capabilities, key) : null),
});

export const EntitlementsContext = React.createContext(buildEntitlements({ phase: "loading" }));

export const useEntitlements = () => React.useContext(EntitlementsContext);
